import { authEvents } from './auth-events';
import { authLogger } from './logger';
import type { User } from '@/types/user';

const TOKEN_STORAGE_KEY = 'auth_token';
const USER_STORAGE_KEY = 'auth_user';

/**
 * Bearer-token storage for the web client. Plain localStorage by design —
 * mirrors Apple Keychain / Android EncryptedSharedPreferences in role, with
 * the same threat model: the OS-level secret store guards platform integrity,
 * but the JS bundle that would do app-level encryption ships the key alongside
 * the ciphertext, so any client-side encryption is theater. Real auth security
 * lives at the Sanctum layer: tokens are opaque, server-revocable, and any 401
 * triggers logout via AuthErrorHandler. No cookies — Next middleware does not
 * gate on auth; routing decisions happen in the client (parity with mobile).
 */
export class AuthTokenService {
  private static broadcastChannel?: BroadcastChannel;
  private static readonly tabId = Math.random().toString(36).substring(2, 15);

  // Synchronous token cache for `getTokenSync()` so ApiClient can attach the
  // Authorization header on the very first request, before any React effect
  // has had a chance to run `initialize()`.
  private static inMemoryToken: string | null = null;

  static {
    if (typeof window !== 'undefined') {
      try {
        this.inMemoryToken = window.localStorage.getItem(TOKEN_STORAGE_KEY);
      } catch {
        this.inMemoryToken = null;
      }
    }
  }

  static {
    if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
      this.broadcastChannel = new BroadcastChannel('auth_refresh_channel');

      this.broadcastChannel.onmessage = (event) => {
        if (event.data.tabId === this.tabId) {
          return;
        }

        if (event.data.type === 'logout') {
          authLogger.info('Logout event received from another tab');
          authEvents.emitLogout();
          this.clearAuth().then(() => {
            window.location.replace('/auth/login');
          });
        } else if (event.data.type === 'login') {
          authLogger.info(
            'Login event received from another tab, reloading page'
          );
          window.location.reload();
        }
      };
    }
  }

  /**
   * Re-emit current token so listeners (ApiClient) wired up after module load
   * pick it up. Must run before the first protected fetch — see
   * StoreInitializer.
   */
  static async initialize(): Promise<void> {
    if (typeof window === 'undefined') return;

    const token = this.getTokenSync();
    if (token) {
      authEvents.emit('token:updated', token);
    }
  }

  static async setToken(token: string | null): Promise<void> {
    if (typeof window === 'undefined') return;

    if (token) {
      this.inMemoryToken = token;
      window.localStorage.setItem(TOKEN_STORAGE_KEY, token);
      authEvents.emit('token:updated', token);
    } else {
      await this.clearAuth();
    }
  }

  static async getToken(): Promise<string | null> {
    if (typeof window === 'undefined') return null;
    return window.localStorage.getItem(TOKEN_STORAGE_KEY);
  }

  static getTokenSync(): string | null {
    return this.inMemoryToken;
  }

  static async setUser(user: User | null): Promise<void> {
    if (typeof window === 'undefined') return;

    if (user) {
      window.localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
    } else {
      window.localStorage.removeItem(USER_STORAGE_KEY);
    }
  }

  static async getUser(): Promise<User | null> {
    if (typeof window === 'undefined') return null;

    const raw = window.localStorage.getItem(USER_STORAGE_KEY);
    if (!raw) return null;

    try {
      return JSON.parse(raw) as User;
    } catch {
      window.localStorage.removeItem(USER_STORAGE_KEY);
      return null;
    }
  }

  static async clearAuth(): Promise<void> {
    if (typeof window === 'undefined') return;

    this.inMemoryToken = null;
    window.localStorage.removeItem(TOKEN_STORAGE_KEY);
    window.localStorage.removeItem(USER_STORAGE_KEY);

    authEvents.emit('token:updated', null);
  }

  static async hasToken(): Promise<boolean> {
    return this.getTokenSync() !== null;
  }

  static broadcastLogout(): void {
    if (this.broadcastChannel) {
      authLogger.debug('Broadcasting logout event to other tabs');
      this.broadcastChannel.postMessage({
        type: 'logout',
        timestamp: Date.now(),
        tabId: this.tabId,
      });
    }
  }

  static broadcastLogin(): void {
    if (this.broadcastChannel) {
      authLogger.debug('Broadcasting login event to other tabs');
      this.broadcastChannel.postMessage({
        type: 'login',
        timestamp: Date.now(),
        tabId: this.tabId,
      });
    }
  }
}
